import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar } from './Calendar';
import { ConnectedAccounts } from './ConnectedAccounts';
import { TaskForm } from './task/TaskForm';
import { ReelsStoryTaskForm } from './task/ReelsStoryTaskForm';
import { TaskDetails } from './post/TaskDetails';
import { PostPreview } from './post/PostPreview';
import { Menu, X, ExternalLink, Bug, PlusCircle, ChevronDown, Film, Layers, FileText } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { CreditsDisplay } from './CreditsDisplay';
import { CreditSettings } from './CreditSettings';
import { TaskDebugger } from './TaskDebugger';
import { manualPublishPost } from '../lib/facebookPublisher';
import { ConnectButtons } from './ConnectButtons';
import type { ContentType } from './task/types';

// Columns the calendar, hover preview and task details read from task.posts.
// `content` and ready_posts.metadata (the full AI prompt, ~15 KB per row) are left
// out on purpose: selecting them pushed this load past the statement timeout and
// the calendar came up empty. The selected post is re-read in full (fetchFullPost)
// and the hover preview loads its text on demand.
const POST_LIST_COLUMNS = [
  'id', 'task_id', 'user_id', 'platform', 'status', 'publication_status',
  'created_at', 'updated_at', 'published_at',
  'platform_post_id', 'platform_post_url', 'platform_post_created_at',
  'execution_id', 'publication_attempt_count', 'last_publish_attempt',
  'content_type', 'image_url', 'metadata'
].join(',');

const READY_POST_LIST_COLUMNS = [
  'id', 'task_id', 'user_id', 'platform', 'status', 'is_default', 'publication_status',
  'created_at', 'updated_at',
  'platform_post_id', 'platform_post_url', 'platform_post_created_at',
  'execution_id', 'image_url'
].join(',');

type PickedPost = { row: any; table: 'posts' | 'ready_posts' };

const isSameLocalDay = (date1: Date, date2: string) => {
  const d2 = new Date(date2);
  const d2Normalized = new Date(d2.getFullYear(), d2.getMonth(), d2.getDate());
  return date1.getTime() === d2Normalized.getTime();
};

// Picks the post shown for the clicked occurrence of a task.
// Priorytet: published post > default ready post > ready post > any ready post
const pickPostForDay = (task: any, posts: any[], readyPosts: any[]): PickedPost | null => {
  const taskDate = new Date(task.scheduled_for);
  const taskDateNormalized = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
  const taskPosts = posts.filter(post => post.task_id === task.id);
  const taskReadyPosts = readyPosts.filter(post => post.task_id === task.id);

  const publishedPost = taskPosts.find(post =>
    (post.status === 'published' || post.platform_post_id || post.platform_post_url) &&
    (post.published_at && isSameLocalDay(taskDateNormalized, post.published_at))
  );
  if (publishedPost) return { row: publishedPost, table: 'posts' };

  const defaultReadyPost = taskReadyPosts.find(post =>
    post.is_default === true &&
    post.created_at &&
    isSameLocalDay(taskDateNormalized, post.created_at)
  );
  if (defaultReadyPost) return { row: defaultReadyPost, table: 'ready_posts' };

  const readyPost = taskPosts.find(post =>
    post.status === 'ready' &&
    post.created_at &&
    isSameLocalDay(taskDateNormalized, post.created_at)
  );
  if (readyPost) return { row: readyPost, table: 'posts' };

  const anyReadyPost = taskReadyPosts.find(post =>
    post.created_at &&
    isSameLocalDay(taskDateNormalized, post.created_at)
  );
  if (anyReadyPost) return { row: anyReadyPost, table: 'ready_posts' };

  return null;
};

export function PostScheduler() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [scheduledTasks, setScheduledTasks] = useState<any[]>([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any>(null);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDebugger, setShowDebugger] = useState(false);
  const [isReelsStoryFormOpen, setIsReelsStoryFormOpen] = useState(false);
  const [reelsStoryDefaultType, setReelsStoryDefaultType] = useState<ContentType>('reel');
  const [showNewTaskMenu, setShowNewTaskMenu] = useState(false);
  const newTaskMenuRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (newTaskMenuRef.current && !newTaskMenuRef.current.contains(event.target as Node)) {
        setShowNewTaskMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openReelsStoryForm = (type: ContentType) => {
    setReelsStoryDefaultType(type);
    setIsReelsStoryFormOpen(true);
    setShowNewTaskMenu(false);
  };

  // Helper: paginated fetch to bypass PostgREST max-rows (default 1000)
  const fetchAllRows = useCallback(async (
    table: string,
    query: { select: string; filters: Record<string, any>; gte?: { col: string; val: string }; neq?: { col: string; val: string } },
    pageSize = 500
  ) => {
    const allRows: any[] = [];
    let from = 0;
    let hasMore = true;

    while (hasMore) {
      let q = supabase.from(table).select(query.select);
      for (const [key, value] of Object.entries(query.filters)) {
        q = q.eq(key, value);
      }
      if (query.gte) q = q.gte(query.gte.col, query.gte.val);
      if (query.neq) q = q.neq(query.neq.col, query.neq.val);

      // Stable order, otherwise range pages can skip or repeat rows
      const { data, error } = await q.order('id').range(from, from + pageSize - 1);
      if (error) throw error;

      const rows = data || [];
      allRows.push(...rows);
      hasMore = rows.length === pageSize;
      from += pageSize;
    }
    return allRows;
  }, []);

  // The selected post is always re-read in full, so PostPreview gets content and
  // the complete metadata (it spreads metadata back into updates).
  const fetchFullPost = useCallback(async (picked: PickedPost | null) => {
    if (!picked) return null;
    const { data, error } = await supabase
      .from(picked.table)
      .select('*')
      .eq('id', picked.row.id)
      .maybeSingle();

    if (error || !data) {
      console.error(`[fetchFullPost] ${picked.table}/${picked.row.id}:`, error);
      return picked.table === 'ready_posts' ? { ...picked.row, table: 'ready_posts' } : picked.row;
    }
    return picked.table === 'ready_posts' ? { ...data, table: 'ready_posts' } : data;
  }, []);

  const selectedTaskRef = useRef<any>(null);
  useEffect(() => {
    selectedTaskRef.current = selectedTask;
  }, [selectedTask]);

  const lastLoadedRef = useRef<{ tasks?: any[]; posts?: any[]; readyPosts?: any[] }>({});
  const loadInFlightRef = useRef<Promise<void> | null>(null);
  const reloadQueuedRef = useRef(false);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const loadTasksRef = useRef<() => Promise<void>>(async () => {});

  const loadTasksOnce = useCallback(async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('No authenticated user found');
        return;
      }

      // FIX: Supabase PostgREST silently caps .limit() to its server-side max-rows (default 1000).
      // Users with 1000+ active tasks had their newest tasks silently dropped,
      // causing posts to never appear in the calendar.
      // Solution: Use range-based pagination to load ALL rows in batches of 500.
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const cutoffDate = ninetyDaysAgo.toISOString();

      // allSettled: one slow table must not blank the whole calendar.
      // A part that fails falls back to what it returned last time.
      const [activeRes, recentRes, postsRes, readyRes] = await Promise.allSettled([
        // Active tasks — paginated to get ALL (bypasses 1000-row cap)
        fetchAllRows('scheduled_tasks', {
          select: '*, connected_accounts(name, platform)',
          filters: { user_id: user.id, status: 'active' }
        }),
        // Recently completed tasks (for showing published posts in calendar history)
        fetchAllRows('scheduled_tasks', {
          select: '*, connected_accounts(name, platform)',
          filters: { user_id: user.id },
          neq: { col: 'status', val: 'active' },
          gte: { col: 'scheduled_for', val: cutoffDate }
        }),
        // Posts — paginated, list columns only
        fetchAllRows('posts', {
          select: POST_LIST_COLUMNS,
          filters: { user_id: user.id },
          gte: { col: 'created_at', val: cutoffDate }
        }),
        // Ready posts — paginated, list columns only
        fetchAllRows('ready_posts', {
          select: READY_POST_LIST_COLUMNS,
          filters: { user_id: user.id },
          neq: { col: 'status', val: 'superseded' },
          gte: { col: 'created_at', val: cutoffDate }
        })
      ]);

      const last = lastLoadedRef.current;
      const failures = [activeRes, recentRes, postsRes, readyRes]
        .filter((r): r is PromiseRejectedResult => r.status === 'rejected');
      failures.forEach(f => console.error('[loadTasks] part failed:', f.reason));

      const tasksFailed = activeRes.status === 'rejected' || recentRes.status === 'rejected';
      const allTasks = tasksFailed
        ? last.tasks
        : [...(activeRes as PromiseFulfilledResult<any[]>).value, ...(recentRes as PromiseFulfilledResult<any[]>).value];
      const allPosts = postsRes.status === 'fulfilled' ? postsRes.value : last.posts;
      const allReadyPosts = readyRes.status === 'fulfilled' ? readyRes.value : last.readyPosts;

      if (failures.length > 0) {
        if (!allTasks) {
          toast.error('Failed to load scheduled tasks', { id: 'load-tasks-error' });
        } else {
          toast.error('Some posts could not be loaded, retrying…', { id: 'load-tasks-error' });
        }
        if (retryCountRef.current < 3) {
          retryCountRef.current += 1;
          if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(() => loadTasksRef.current(), 5000 * retryCountRef.current);
        }
      } else {
        retryCountRef.current = 0;
        toast.dismiss('load-tasks-error');
      }
      if (!allTasks) return;

      lastLoadedRef.current = {
        tasks: allTasks,
        posts: allPosts ?? last.posts,
        readyPosts: allReadyPosts ?? last.readyPosts
      };

      // Merge active + recent completed, deduplicate by id
      const taskMap = new Map();
      allTasks.forEach((t: any) => taskMap.set(t.id, t));
      const tasks = Array.from(taskMap.values());

      // Filter out silently expired posts - these were stuck in queue and auto-cleaned
      const posts = (allPosts || []).filter((post: any) => !post.metadata?.silently_expired);
      const readyPosts = allReadyPosts || [];

      console.log(`[loadTasks] Tasks: ${tasks.length}, Posts: ${posts.length}, ReadyPosts: ${readyPosts.length}, Failed parts: ${failures.length}`);

      const postsByTask = new Map<string, any[]>();
      const pushByTask = (post: any) => {
        const list = postsByTask.get(post.task_id);
        if (list) list.push(post); else postsByTask.set(post.task_id, [post]);
      };
      posts.forEach(pushByTask);
      readyPosts
        .filter((post: any) => post.status !== 'expired')
        .forEach((post: any) => pushByTask({ ...post, table: 'ready_posts' }));

      const tasksWithPosts = tasks.map((task: any) => ({
        ...task,
        posts: postsByTask.get(task.id) || []
      }));

      setScheduledTasks(tasksWithPosts);

      const current = selectedTaskRef.current;
      if (current && tasksWithPosts.some((t: any) => t.id === current.id)) {
        // Use selectedTask.scheduled_for (the clicked occurrence), not selectedDate
        const picked = pickPostForDay(current, posts, readyPosts);
        const fullPost = await fetchFullPost(picked);
        // The user may have clicked another task meanwhile
        if (selectedTaskRef.current?.id === current.id) {
          setSelectedPost(fullPost);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load scheduled tasks', { id: 'load-tasks-error' });
    } finally {
      setIsLoading(false);
    }
  }, [fetchAllRows, fetchFullPost]);

  // Coalesces bursts: while a load runs, further calls queue exactly one more run.
  const loadTasks = useCallback(async () => {
    if (loadInFlightRef.current) {
      reloadQueuedRef.current = true;
      return loadInFlightRef.current;
    }
    const run = (async () => {
      do {
        reloadQueuedRef.current = false;
        await loadTasksOnce();
      } while (reloadQueuedRef.current);
    })();
    loadInFlightRef.current = run;
    try {
      await run;
    } finally {
      loadInFlightRef.current = null;
    }
  }, [loadTasksOnce]);
  loadTasksRef.current = loadTasks;

  const handleTaskSelect = useCallback(async (task: any) => {
    setSelectedTask(task);
    selectedTaskRef.current = task;

    // Natychmiast znajdź i ustaw odpowiedni post
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const [postsResponse, readyPostsResponse] = await Promise.all([
        supabase
          .from('posts')
          .select(POST_LIST_COLUMNS)
          .eq('task_id', task.id)
          .eq('user_id', user.id),
        supabase
          .from('ready_posts')
          .select(READY_POST_LIST_COLUMNS)
          .eq('task_id', task.id)
          .eq('user_id', user.id)
          .neq('status', 'superseded')
      ]);

      if (postsResponse.error) console.error('[handleTaskSelect] posts:', postsResponse.error);
      if (readyPostsResponse.error) console.error('[handleTaskSelect] ready_posts:', readyPostsResponse.error);

      const picked = pickPostForDay(task, postsResponse.data || [], readyPostsResponse.data || []);
      const fullPost = await fetchFullPost(picked);
      if (selectedTaskRef.current?.id === task.id) {
        setSelectedPost(fullPost);
      }
    } catch (error) {
      console.error('Error loading post for task:', error);
      setSelectedPost(null);
    }
  }, [fetchFullPost]);

  const handleManualPublish = async (task: any) => {
    if (isPublishing) return;

    try {
      // For recurring tasks, check if published TODAY
      // For once tasks, check if published at all
      let checkQuery = supabase
        .from('posts')
        .select('id')
        .eq('task_id', task.id)
        .eq('status', 'published');

      if (task.cycle !== 'once') {
        // For recurring tasks: check if published today
        const today = new Date().toISOString().split('T')[0];
        checkQuery = checkQuery.gte('created_at', today + 'T00:00:00')
                                .lt('created_at', today + 'T23:59:59');
      }

      const { data: existingPosts, error: checkError } = await checkQuery.limit(1);

      if (checkError) throw checkError;

      if (existingPosts && existingPosts.length > 0) {
        toast.error('This task already has a published post' + (task.cycle !== 'once' ? ' today' : ''));
        return;
      }

      setIsPublishing(true);
      const publishToast = toast.loading('Publishing post...');

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          throw new Error('Not authenticated');
        }

        // Call the manual_publish_post function
        const result = await manualPublishPost(task.id, user.id);

        if (!result) {
          throw new Error('No response received from publishing service');
        }

        if (!result.success) {
          throw new Error(result.error || 'Publishing service returned empty response');
        }

        toast.dismiss(publishToast);
        toast.success('Post published successfully');
        await loadTasks();
      } catch (error) {
        toast.dismiss(publishToast);
        throw error;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ?
        error.message :
        typeof error === 'object' ?
          (error as any)?.message || JSON.stringify(error) :
          'Unknown error occurred';

      console.error('Manual publish error details:', error);
      toast.error(`Manual publish error: ${errorMessage}`);
    } finally {
      setIsPublishing(false);
    }
  };

  useEffect(() => {
    loadTasks();

    // Subscribe once per mount, only to this user's rows, and debounce: one
    // publish touches a post several times and each event used to trigger a
    // full reload of the calendar.
    let disposed = false;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;
    const channels: ReturnType<typeof supabase.channel>[] = [];

    const scheduleReload = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => loadTasksRef.current(), 1500);
    };

    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || disposed) return;

      for (const table of ['scheduled_tasks', 'posts', 'ready_posts']) {
        channels.push(
          supabase
            .channel(`${table}-changes-${user.id}`)
            .on('postgres_changes', {
              event: '*',
              schema: 'public',
              table,
              filter: `user_id=eq.${user.id}`
            }, scheduleReload)
            .subscribe()
        );
      }
    })();

    return () => {
      disposed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      channels.forEach(channel => supabase.removeChannel(channel));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFormOpen) {
      setIsSidebarOpen(false);
    }
  }, [isFormOpen]);

  const handleTaskAction = async (task: any, action: 'edit' | 'delete' | 'toggle') => {
    try {
      if (action === 'edit') {
        if (task.content_type === 'reel' || task.content_type === 'story') {
          setEditingTask(task);
          setReelsStoryDefaultType(task.content_type);
          setIsReelsStoryFormOpen(true);
        } else {
          setEditingTask(task);
          setIsFormOpen(true);
        }
      } else if (action === 'delete') {
        if (isDeleting) return;
        
        setIsDeleting(true);
        const deleteToast = toast.loading('Deleting task...');

        try {
          const { data: { user } } = await supabase.auth.getUser();
          if (!user) throw new Error('Not authenticated');

          const { data, error } = await supabase.rpc('delete_task', {
            p_task_id: task.id,
            p_user_id: user.id
          });

          if (error) {
            throw new Error(`Failed to delete task: ${error.message}`);
          }

          if (data) {
            setScheduledTasks(tasks => tasks.filter(t => t.id !== task.id));
            setSelectedTask(null);
            toast.dismiss(deleteToast);
            toast.success('Task deleted successfully');
          } else {
            throw new Error('Failed to delete task - not found or unauthorized');
          }
        } catch (error) {
          toast.dismiss(deleteToast);
          throw error;
        }
      } else if (action === 'toggle') {
        const newStatus = task.status === 'active' ? 'inactive' : 'active';
        const toggleToast = toast.loading(`${newStatus === 'active' ? 'Activating' : 'Deactivating'} task...`);

        try {
          const { error } = await supabase
            .from('scheduled_tasks')
            .update({ status: newStatus })
            .eq('id', task.id);

          if (error) throw error;

          setScheduledTasks(tasks =>
            tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t)
          );

          if (selectedTask && selectedTask.id === task.id) {
            setSelectedTask(prev => prev ? { ...prev, status: newStatus } : prev);
          }

          toast.dismiss(toggleToast);
          toast.success(`Task ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`);
        } catch (error) {
          toast.dismiss(toggleToast);
          throw error;
        }
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('Error managing task:', errorMessage);
      toast.error(`Failed to manage task: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Check if the selected task has a published post FOR TODAY'S OCCURRENCE
  // For recurring tasks, only check posts from today
  const hasPublishedPost = selectedTask?.posts?.some((post: any) => {
    if (!post.status || (post.status !== 'published' && !post.platform_post_id && !post.platform_post_url)) {
      return false;
    }

    // For recurring tasks, check if there's a published post from today's occurrence
    // Compare against today's date, not task.scheduled_for (which may be in the future)
    if (selectedTask.cycle && selectedTask.cycle !== 'once') {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const postDate = new Date(post.created_at);
      postDate.setHours(0, 0, 0, 0);
      return postDate.getTime() === today.getTime();
    }

    return true;
  });

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="lg:hidden bg-white border-b px-4 py-3 flex items-center justify-between sticky top-0 z-20">
        <button
          onClick={() => setIsSidebarOpen(!isSidebarOpen)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <Menu className="w-6 h-6" />
        </button>
        <h1 className="text-lg font-semibold">Social Media Scheduler</h1>
        <div className="relative" ref={newTaskMenuRef}>
          <button
            onClick={() => setShowNewTaskMenu(!showNewTaskMenu)}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg flex items-center gap-1"
          >
            <PlusCircle className="w-6 h-6" />
            <ChevronDown className="w-3 h-3" />
          </button>
          {showNewTaskMenu && (
            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border z-50 py-1">
              <button
                onClick={() => { setEditingTask(null); setIsFormOpen(true); setShowNewTaskMenu(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
              >
                <FileText className="w-4 h-4 text-blue-600" />
                <span>Nowy Post</span>
              </button>
              <button
                onClick={() => openReelsStoryForm('reel')}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 transition-colors"
              >
                <Film className="w-4 h-4 text-purple-600" />
                <span>Nowy Reel</span>
              </button>
              <button
                onClick={() => openReelsStoryForm('story')}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 transition-colors"
              >
                <Layers className="w-4 h-4 text-pink-600" />
                <span>Nowa Story</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {isSidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <div
        className={`
          lg:hidden fixed inset-y-0 left-0 w-full max-w-[320px] bg-white z-40 transform transition-transform duration-200 ease-in-out overflow-auto
          ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">Connected Accounts</h2>
          <button
            onClick={() => setIsSidebarOpen(false)}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4">
          <ConnectedAccounts />
        </div>
      </div>

      <div className="lg:flex">
        <div className="hidden lg:block lg:w-80 xl:w-96 p-6 border-r bg-white min-h-screen sticky top-0">
          <ConnectedAccounts />
        </div>

        <div className="flex-1 p-4 lg:p-6">
          <div className="max-w-6xl mx-auto">
            <div className="hidden lg:flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">Social Media Scheduler</h1>
              <div className="flex items-center gap-4">
                <CreditsDisplay />
                <img 
                  src="https://gregciupek.com/wp-content/uploads/2025/02/grafikaautomatyzacji.gif"
                  alt="AI Automation"
                  className="h-16 w-auto"
                />
                <a
                  href="https://panel.aiagenthub.pl"
                  target="_self"
                  className="flex items-center gap-2 bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 
                    focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Dashboard
                  <ExternalLink className="w-4 h-4" />
                </a>
                <div className="relative">
                  <div className="flex items-center">
                    <button
                      onClick={() => { setEditingTask(null); setIsFormOpen(true); }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-l-lg hover:bg-blue-700 
                        focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      New Task
                    </button>
                    <button
                      onClick={() => setShowNewTaskMenu(!showNewTaskMenu)}
                      className="bg-blue-700 text-white px-2 py-2 rounded-r-lg hover:bg-blue-800 border-l border-blue-500"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                  {showNewTaskMenu && (
                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-xl border z-50 py-1">
                      <button
                        onClick={() => { setEditingTask(null); setIsFormOpen(true); setShowNewTaskMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 transition-colors"
                      >
                        <FileText className="w-4 h-4 text-blue-600" />
                        <span>Nowy Post</span>
                      </button>
                      <button
                        onClick={() => openReelsStoryForm('reel')}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-purple-50 transition-colors"
                      >
                        <Film className="w-4 h-4 text-purple-600" />
                        <span>Nowy Reel</span>
                      </button>
                      <button
                        onClick={() => openReelsStoryForm('story')}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-pink-50 transition-colors"
                      >
                        <Layers className="w-4 h-4 text-pink-600" />
                        <span>Nowa Story</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mb-6">
              <CreditSettings />
            </div>

            <div className="mb-4 flex justify-end">
              <button
                onClick={() => setShowDebugger(!showDebugger)}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                <Bug className="w-4 h-4" />
                <span>{showDebugger ? 'Hide Debugger' : 'Show Debugger'}</span>
              </button>
            </div>

            {showDebugger && (
              <div className="mb-6">
                <TaskDebugger />
              </div>
            )}

            <ConnectButtons />

            <div className="space-y-6">
              <Calendar
                selectedDate={selectedDate}
                onDateSelect={setSelectedDate}
                scheduledTasks={scheduledTasks || []}
                onTaskSelect={handleTaskSelect}
              />

              {selectedTask && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <TaskDetails
                    task={selectedTask}
                    onAction={(action) => handleTaskAction(selectedTask, action)}
                  />
                  <div className="bg-white rounded-lg shadow p-4">
                    <div className="h-full flex flex-col">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold">Post Preview</h3>
                        <div className="flex gap-2">
                          {selectedTask.status === 'active' && (
                            <button
                              onClick={() => handleManualPublish(selectedTask)}
                              disabled={isPublishing || isDeleting || hasPublishedPost}
                              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                                ${hasPublishedPost ? 'bg-gray-500' : 'bg-green-500 hover:bg-green-600'} text-white`}
                              title={hasPublishedPost ? 'This task already has a published post' : ''}
                            >
                              {isPublishing ? 'Publishing...' : hasPublishedPost ? 'Already Published' : 'Publish Now'}
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex-1">
                        <PostPreview 
                          post={selectedPost} 
                          task={selectedTask} 
                          onPostUpdate={loadTasks}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block fixed top-[120px] right-4 z-10">
        <div className="flex flex-col gap-2">
          <button
            onClick={() => {
              setEditingTask(null);
              setIsFormOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-blue-700
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <PlusCircle className="w-5 h-5" />
            <span>New Task</span>
          </button>
          <button
            onClick={() => openReelsStoryForm('reel')}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg hover:bg-purple-700
              focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <Film className="w-4 h-4" />
            <span>Reel</span>
          </button>
          <button
            onClick={() => openReelsStoryForm('story')}
            className="bg-gradient-to-r from-pink-500 to-orange-500 text-white px-4 py-2 rounded-lg shadow-lg hover:from-pink-600 hover:to-orange-600
              focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 flex items-center gap-2"
          >
            <Layers className="w-4 h-4" />
            <span>Story</span>
          </button>
        </div>
      </div>

      {isFormOpen && (
        <div className="fixed inset-0 z-[60]">
          <div
            className="absolute inset-0 bg-black bg-opacity-50 lg:bg-opacity-0"
            onClick={() => {
              setIsFormOpen(false);
              setEditingTask(null);
            }}
          />
          <div className="absolute inset-y-0 right-0 w-full sm:w-[480px] bg-white shadow-xl">
            <TaskForm
              onClose={() => {
                setIsFormOpen(false);
                setEditingTask(null);
              }}
              onSubmit={loadTasks}
              editingTask={editingTask}
            />
          </div>
        </div>
      )}

      {isReelsStoryFormOpen && (
        <ReelsStoryTaskForm
          onClose={() => {
            setIsReelsStoryFormOpen(false);
            setEditingTask(null);
          }}
          onSubmit={() => {
            setIsReelsStoryFormOpen(false);
            setEditingTask(null);
            loadTasks();
          }}
          editingTask={editingTask}
          defaultContentType={reelsStoryDefaultType}
        />
      )}
    </div>
  );
}